import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TomadorInfo } from './TomadorInfo';
import { ClientInfo } from './ClientInfo';
import { ValidateField, validateProvinceAndPopulation } from './ValidateInfo';
import { useLocation } from '../../../../hooks/useLocation';
import { checkIfIsDNI } from '../../../../helpers/checkIfIsDNI';
import { checkIfIsNIE } from '../../../../helpers/checkIfIsNIE';
import { getProvinceAndMunicipalityByZipCode } from '../../../../actions/locationAction';
import { validateZipCode } from '../../../../validators/validateZipCode';
import { spainIdType } from '../../../../validators/validateSpanishID';

export const FormInfo = ({ formValues, handleOnChange, setValues, formType, layoutSize }) => {
  const dispatch = useDispatch();
  const isTomador = formType === 'tomador';

  const [fieldErrors, setFieldErrors] = useState({});

  const nif = formValues.NIF?.trim() ?? '';
  const isNifValid = nif !== '' && fieldErrors.NIF === false;

  const validateField = (name, value, currentValues = formValues) => {
    const updatedValues = {
      ...currentValues,
      [name]: value,
    };

    const isValid = ValidateField(formType, updatedValues, name, value);
    const hasError = !isValid;

    setFieldErrors((previousErrors) => {
      const updatedErrors = {
        ...previousErrors,
        [name]: hasError,
      };

      if (name === 'FechaNacimiento' && updatedValues.FechaCarne) {
        const isLicenceDateValid = ValidateField(formType, updatedValues, 'FechaCarne', updatedValues.FechaCarne);

        updatedErrors.FechaCarne = !isLicenceDateValid;
      }

      return updatedErrors;
    });

    return hasError;
  };

  const handleFieldChange = (event) => {
    const { name } = event.target;
    const value = name === 'NIF' ? event.target.value.toUpperCase() : event.target.value;

    handleOnChange({
      target: {
        name,
        value,
      },
    });

    validateField(name, value);
  };

  // NIF
  useEffect(() => {
    if (!isNifValid) return;

    const documentType = spainIdType(nif);

    setValues((previousValues) => {
      if (previousValues.TipoDocumento === documentType) {
        return previousValues;
      }

      return {
        ...previousValues,
        TipoDocumento: documentType,
      };
    });
  }, [isNifValid, nif, setValues]);

  // COUNTRIES
  const { Countries } = useLocation();
  const [listCountries, setListCountries] = useState(Countries ?? []);

  useEffect(() => {
    if (!isTomador) return;

    if (!isNifValid) {
      setListCountries(Countries ?? []);
      return;
    }

    if (checkIfIsDNI(nif)) {
      setListCountries((Countries ?? []).filter((country) => country.CountryCode === 'ESP'));
      return;
    }

    if (checkIfIsNIE(nif)) {
      setListCountries((Countries ?? []).filter((country) => country.CountryCode !== 'ESP'));
      return;
    }

    setListCountries(Countries ?? []);
  }, [isTomador, isNifValid, nif, Countries]);

  // NACIONALIDAD
  useEffect(() => {
    if (!isTomador || !isNifValid || listCountries.length === 0) {
      return;
    }

    let nacionality = formValues.Nacionality;

    if (listCountries.length === 1) {
      nacionality = listCountries[0].CountryCode;
    } else {
      const currentCountryIsAllowed = listCountries.some((country) => country.CountryCode === formValues.Nacionality);

      if (!currentCountryIsAllowed) {
        nacionality = '';
      }
    }

    if (nacionality === formValues.Nacionality) {
      return;
    }

    setValues((previousValues) => ({
      ...previousValues,
      Nacionality: nacionality,
    }));
  }, [isTomador, isNifValid, listCountries, formValues.Nacionality, setValues]);

  // FECHAS
  const lastCommittedDate = useRef({});

  const commitDateField = (name, value) => {
    const normalizedValue = typeof value === 'string' ? value.trim() : '';

    if (normalizedValue === formValues[name] || lastCommittedDate.current[name] === normalizedValue) {
      return;
    }

    lastCommittedDate.current[name] = normalizedValue;

    handleFieldChange({
      target: {
        name,
        value: normalizedValue,
      },
    });
  };

  // CP, PROVINCIA Y POBLACION
  useEffect(() => {
    let cancelled = false;

    const codigoPostal = formValues.CodigoPostal?.trim() ?? '';

    const clearLocation = (previousValues) => ({
      ...previousValues,
      Provincia: '',
      IdProvince: '',
      Poblacion: '',
      IdPoblacion: '',
    });
    setValues(clearLocation);

    if (!codigoPostal || !validateZipCode(codigoPostal)) {
      return () => {
        cancelled = true;
      };
    }

    async function fetchData() {
      try {
        const info = await getProvinceAndMunicipalityByZipCode(codigoPostal, dispatch);

        if (cancelled) return;

        const provincia = info?.ProvinceDescription ?? '';
        const idProvince = info?.ProvinceCode ?? '';
        const poblacion = info?.PopulationDescription ?? '';
        const idPoblacion = info?.PopulationCode ?? '';

        const locationIsValid = validateProvinceAndPopulation(idProvince, idPoblacion);

        setValues((previousValues) => ({
          ...previousValues,
          Provincia: provincia,
          IdProvince: idProvince,
          Poblacion: poblacion,
          IdPoblacion: idPoblacion,
        }));

        setFieldErrors((previousErrors) => ({
          ...previousErrors,
          CodigoPostal: !locationIsValid,
        }));
      } catch {
        if (cancelled) return;

        setValues(clearLocation);

        setFieldErrors((previousErrors) => ({
          ...previousErrors,
          CodigoPostal: true,
        }));
      }
    }

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [formValues.CodigoPostal]);

  const LayoutComponent = isTomador ? TomadorInfo : ClientInfo;

  return (
    <LayoutComponent
      formValues={formValues}
      fieldErrors={fieldErrors}
      handleFieldChange={handleFieldChange}
      commitDateField={commitDateField}
      formType={formType}
      listCountries={listCountries}
      layoutSize={layoutSize}
    />
  );
};
